import { subDays } from "date-fns";
import { isEqual } from "lodash-es";
import {
  computed,
  ComputedRef,
  reactive,
  Ref,
  ref,
  toRaw,
  watch,
  WatchStopHandle,
} from "vue";

import controller from "@ctrl/controller";
import type { CalendarTasks, Controller, Week } from "@ctrl/controller-rpc";

import { bindAsync } from "@/ref";

const emptyBatch: CalendarTasks = {
  scheduled: Array(7).fill({ todo: [], other: [] }),
  postponed: [],
  overdue: [],
  upcoming: [],
  unplanned: [],
};

const { datesOfWeek } = controller;

export const useTasks = async (
  controller: Controller,
  week: ComputedRef<Week>,
  today: Ref<Date>,
  isWeekStartOnSunday: Ref<boolean>,
  /**
   * Whether the task batch should be live (the planner's sidebar batch only
   * loads while the left pane is visible; its footer batch only in week
   * views with the bottom pane shown). While false, nothing is fetched and
   * the `calendarTasks` subscription is released.
   */
  isEnabled: Ref<boolean>
) => {
  const isLoadingTasks = ref<boolean>(true);
  const scheduledTaskIds = ref<CalendarTasks["scheduled"]>(
    emptyBatch.scheduled
  );
  // "Postponed" is the controller's name for tasks planned for the week
  // without a day; the UI calls this the Week section.
  const weekTaskIds = ref<CalendarTasks["postponed"]>(emptyBatch.postponed);
  const overdueTaskIds = ref<CalendarTasks["overdue"]>(emptyBatch.overdue);
  const upcomingTaskIds = ref<CalendarTasks["upcoming"]>(emptyBatch.upcoming);
  const unplannedTaskIds = ref<CalendarTasks["unplanned"]>(
    emptyBatch.unplanned
  );
  const watchers = reactive<{ unwatch: WatchStopHandle; release: () => void }>({
    unwatch: () => {},
    release: () => {},
  });

  const weekDate: ComputedRef<Date> = computed(() => {
    // HACK: datesOfWeek does not take into account the week start day
    const [_start, _end] = datesOfWeek(week.value);
    const start = subDays(_start, isWeekStartOnSunday.value ? 1 : 0);
    const end = subDays(_end, isWeekStartOnSunday.value ? 1 : 0);
    if (start <= today.value && today.value <= end) {
      return today.value;
    } else if (start < today.value) {
      return end;
    } else {
      return start;
    }
  });
  const buildTaskIds = (
    calendarTasks: CalendarTasks | null | undefined
  ): void => {
    isLoadingTasks.value = true;
    if (calendarTasks) {
      // Required to deproxify
      const newScheduledTaskIds: CalendarTasks["scheduled"] = toRaw(
        calendarTasks.scheduled
      );
      const newWeekTaskIds: CalendarTasks["postponed"] = toRaw(
        calendarTasks.postponed
      );
      const newOverdueTaskIds: CalendarTasks["overdue"] = toRaw(
        calendarTasks.overdue
      );
      const newUpcomingTaskIds: CalendarTasks["upcoming"] = toRaw(
        calendarTasks.upcoming
      );
      const newUnplannedTaskIds: CalendarTasks["unplanned"] = toRaw(
        calendarTasks.unplanned
      );
      if (!isEqual(scheduledTaskIds.value, newScheduledTaskIds))
        scheduledTaskIds.value = newScheduledTaskIds;
      if (!isEqual(weekTaskIds.value, newWeekTaskIds))
        weekTaskIds.value = newWeekTaskIds;
      if (!isEqual(overdueTaskIds.value, newOverdueTaskIds))
        overdueTaskIds.value = newOverdueTaskIds;
      if (!isEqual(upcomingTaskIds.value, newUpcomingTaskIds))
        upcomingTaskIds.value = newUpcomingTaskIds;
      if (!isEqual(unplannedTaskIds.value, newUnplannedTaskIds))
        unplannedTaskIds.value = newUnplannedTaskIds;
    }
    isLoadingTasks.value = false;
  };
  const unwatchIsEnabled = watch(
    isEnabled,
    async (isEnabled) => {
      if (isEnabled) {
        const calendarTasks = await bindAsync(weekDate, async (weekDate) => {
          const calendarTasks = await controller.calendarTasks(weekDate);
          return calendarTasks;
        });
        // release() may have run while the batch was in flight (planner
        // discarded mid-load): it released the previous (no-op)
        // `watchers.release`, so the binding landing now must be released
        // instead of installed, or its controller ref leaks.
        if (released) {
          calendarTasks.release();
          return;
        }
        watchers.release = calendarTasks.release;
        watchers.unwatch = watch(
          calendarTasks,
          (calendarTasks) => buildTaskIds(calendarTasks),
          {
            immediate: true,
          }
        );
      } else {
        watchers.release();
        watchers.unwatch();
      }
    },
    { immediate: true }
  );
  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    unwatchIsEnabled();
    watchers.release();
    watchers.unwatch();
  };
  return {
    isLoadingTasks,
    // Releases the watchers and controller refs.
    release,
    scheduledTaskIds,
    weekTaskIds,
    overdueTaskIds,
    upcomingTaskIds,
    unplannedTaskIds,
  };
};
